// ADR 30.9.26ai §11 — the stale-card cache: database `lingara-apps-v1`, one
// object store, `cards`. The web's half of H1's `app_cards_v1`, storing the
// same record (`AppCachedCard`: the card, never the tree, so a newer build's
// per-slot limits apply to an old card).
//
// Hand-written in `offline/offlineDb.ts`' shape, every operation guarded, so a
// refused or blocked IndexedDB makes each one a no-op and the slot behaves as
// if nothing were cached. Expiry is not the store's: a card older than H1's
// `STALE_CARD_MAX_AGE_MS` is simply not usable to `appResolveSlot`.
//
// One database per concern (23.9.26v §1), so no `onupgradeneeded` here can
// break the tutor's or the offline cache's. Types only from the wasm facade:
// the sign-out wipe imports this module from the boot graph.

import type { AppCachedCard, AppSlot } from '../../wasm'

const DB_NAME = 'lingara-apps-v1'
const DB_VERSION = 1
const CARDS = 'cards'
const SEP = '\u001f'

/** H1 §6's key: user, install, slot. */
export function cardKey(userId: string, installId: string, slot: AppSlot): string {
  return [userId, installId, slot].join(SEP)
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB aborted'))
  })
}

/**
 * Reads `indexedDB` at call time (absent in jsdom unless a suite installs one),
 * off `globalThis` — the window in a browser — so the store's own suite can run
 * in vitest's `node` project with a stubbed global.
 */
function openAppsDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(CARDS)
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
      request.onblocked = () => reject(new Error('IndexedDB open blocked'))
    } catch (error) {
      reject(error)
    }
  })
}

async function safely<T>(write: boolean, fallback: T, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  let db: IDBDatabase
  try {
    db = await openAppsDatabase()
  } catch {
    return fallback
  }
  try {
    const tx = db.transaction(CARDS, write ? 'readwrite' : 'readonly')
    const result = await operation(tx.objectStore(CARDS))
    await transactionDone(tx)
    return result
  } catch {
    return fallback
  } finally {
    db.close()
  }
}

/** Deletes every key `drop` selects, in one transaction. */
function deleteWhere(drop: (parts: string[]) => boolean): Promise<void> {
  return safely(true, undefined, async (store) => {
    const keys = (await requestResult(store.getAllKeys())) as IDBValidKey[]
    const doomed = keys.filter((key) => typeof key === 'string' && drop(key.split(SEP)))
    await Promise.all(doomed.map((key) => requestResult(store.delete(key))))
  })
}

export async function getCard(userId: string, installId: string, slot: AppSlot): Promise<AppCachedCard | null> {
  const key = cardKey(userId, installId, slot)
  const found = await safely(false, undefined, (store) => requestResult(store.get(key)) as Promise<unknown>)
  return (found as AppCachedCard | undefined) ?? null
}

export function setCard(userId: string, installId: string, slot: AppSlot, card: AppCachedCard): Promise<void> {
  return safely(true, undefined, async (store) => void (await requestResult(store.put(card, cardKey(userId, installId, slot)))))
}

/** Every slot's card for one install (uninstall, and a relay 404). */
export function deleteInstall(installId: string): Promise<void> {
  return deleteWhere((parts) => parts[1] === installId)
}

/** H1's `retain_installs`: after each list, drop the user's cards of installs no longer listed. */
export function retainInstalls(userId: string, installIds: readonly string[]): Promise<void> {
  return deleteWhere((parts) => parts[0] === userId && !installIds.includes(parts[1]))
}

/** The sign-out wipe (24.9.26h): a third party's card is account data. Every key this module wrote. */
export function clearAppCards(): Promise<void> {
  return deleteWhere((parts) => parts.length === 3)
}
